import { getSupabaseClient } from './supabase';
import { readableErrorMessage } from '../utils/appErrors';

/**
 * The transfer record page (docs/warehouse.md): one reader (`get_transfer_record`) and one RPC per
 * action. Every action takes a request key; the caller generates one per user action and sends the
 * same key again when it retries that action, so a double submit is a no-op on the server. The
 * server decides who may do what: the reader returns `actions.<name>.{allowed, reason}` and each
 * action refuses with a person-readable message (machine key in the error's hint).
 */

export type TransferRecordSource = 'r2' | 'portal';
export type TransferScope = 'temporary' | 'permanent';
export type TransferLineKind = 'item' | 'product' | 'asset' | 'vehicle' | 'external' | 'accessory';
export type TransferLineTracking = 'serial' | 'quantity';
export type TransferSerialStatus = 'assigned' | 'picked' | 'shipped' | 'in_transit' | 'received' | 'kept' | 'cancelled';

export type TransferRecordRef = { id: string; name: string | null };
export type TransferRecordSite = TransferRecordRef & { timezone: string | null };
export type TransferRecordCatalogRef = { id: string; code: string | null; name: string | null };
export type TransferRecordDocRef = { id: string; docNo: string | null; status: string };
export type TransferRecordReturnLeg = TransferRecordDocRef & { expectedShipAt: string | null; lineCount: number };

export type TransferRecordRequest = {
  broadcastId: string | null;
  itemName: string | null;
  qty: number | null;
  requestedBySite: TransferRecordRef | null;
  requestedAt: string | null;
  acceptedBySite: TransferRecordRef | null;
  acceptedAt: string | null;
  status: string;
};

export type TransferRecordHeader = {
  id: string;
  docNo: string | null;
  status: string;
  source: TransferRecordSource;
  description: string | null;
  scope: TransferScope;
  fromSite: TransferRecordSite | null;
  toSite: TransferRecordSite | null;
  needByAt: string | null;
  expectedShipAt: string | null;
  expectedReceiveAt: string | null;
  expectedReturnAt: string | null;
  actualShipAt: string | null;
  actualReceiveAt: string | null;
  confirmedAt: string | null;
  createdAt: string | null;
  createdBy: TransferRecordRef | null;
  logisticsMode: TransferRecordRef | null;
  trackingNo: string | null;
  lastR2ImportAt: string | null;
  daysLate: number | null;
  parent: TransferRecordDocRef | null;
  returnLeg: TransferRecordReturnLeg | null;
  request: TransferRecordRequest | null;
  chatConversationId: string | null;
  /** Messages in the transfer's thread, when the reader returns it (the Chat tab's count). */
  chatMessageCount: number | null;
};

export type TransferRecordTotals = {
  lineCount: number;
  units: number;
  picked: number;
  shipped: number;
  received: number;
  outstanding: number;
  serializedAssets: number;
  shipmentCount: number;
};

export type TransferRecordSerial = {
  id: string;
  assetId: string | null;
  /** What the chip shows: the barcode, else a serial number. */
  code: string;
  /** Every code on the unit (barcode, serial numbers, RFID tag); a scan of any of them matches it. */
  codes: string[];
  status: TransferSerialStatus;
};

export type TransferRecordLine = {
  id: string;
  kind: TransferLineKind;
  item: TransferRecordCatalogRef | null;
  product: TransferRecordCatalogRef | null;
  vehicle: TransferRecordRef | null;
  description: string | null;
  category: string | null;
  tracking: TransferLineTracking;
  qty: number;
  pickedQty: number;
  shippedQty: number;
  receivedQty: number;
  shortQty: number;
  status: string;
  serials: TransferRecordSerial[];
  sourceEvent: { id: string; code: string | null } | null;
  notes: string | null;
};

export type TransferRecordWaypoint = {
  id: string;
  seq: number;
  location: TransferRecordRef | null;
  address: string | null;
  kind: string | null;
  status: string;
  arriveAt: string | null;
  departAt: string | null;
  notes: string | null;
};

export type TransferRecordShipment = {
  id: string;
  carrier: string | null;
  mode: string | null;
  trackingNo: string | null;
  vehicle: TransferRecordRef | null;
  driverName: string | null;
  shippedAt: string | null;
  deliveredAt: string | null;
  notes: string | null;
};

export type TransferRecordEvent = {
  id: string;
  code: string | null;
  name: string | null;
  startDate: string | null;
  endDate: string | null;
  site: TransferRecordRef | null;
  status: string | null;
  unitsOnTransfer: number | null;
  /** A link row ties it to the transfer: Unlink removes it. */
  linked: boolean;
  /** A line on the transfer is for it: it stays listed while that line does, linked or not. */
  onLines: boolean;
};

export type TransferTimelineEntry = {
  id: string;
  at: string;
  kind: string;
  summary: string;
  actor: TransferRecordRef | null;
  details: { [key: string]: unknown } | null;
};

export const TRANSFER_ACTION_NAMES = [
  'confirm',
  'pick',
  'ship',
  'receive',
  'cancel',
  'edit_lines',
  'edit_dates',
  'create_return',
  'add_shipment',
  'add_stop',
  'link_event',
  'report_problem',
] as const;

export type TransferActionName = typeof TRANSFER_ACTION_NAMES[number];
export type TransferActionState = { allowed: boolean; reason: string | null };
export type TransferRecordActions = { [name in TransferActionName]: TransferActionState };

export type TransferRecord = {
  transfer: TransferRecordHeader;
  totals: TransferRecordTotals;
  lines: TransferRecordLine[];
  waypoints: TransferRecordWaypoint[];
  shipments: TransferRecordShipment[];
  events: TransferRecordEvent[];
  timeline: TransferTimelineEntry[];
  actions: TransferRecordActions;
};

type Json = { [key: string]: unknown };

function asObject(value: unknown): Json | null {
  return value && typeof value === 'object' && !Array.isArray(value) ? value as Json : null;
}

function asArray(value: unknown): unknown[] {
  return Array.isArray(value) ? value : [];
}

function asText(value: unknown): string | null {
  if (value == null) return null;
  const text = String(value).trim();
  return text.length > 0 ? text : null;
}

function asNumber(value: unknown, fallback = 0): number {
  const number = typeof value === 'number' ? value : Number(value ?? NaN);
  return Number.isFinite(number) ? number : fallback;
}

function asNullableNumber(value: unknown): number | null {
  if (value == null || value === '') return null;
  const number = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(number) ? number : null;
}

function asRef(value: unknown): TransferRecordRef | null {
  const object = asObject(value);
  const id = asText(object?.id);
  return id ? { id, name: asText(object?.name) } : null;
}

function asSite(value: unknown): TransferRecordSite | null {
  const object = asObject(value);
  const ref = asRef(object);
  return ref ? { ...ref, timezone: asText(object?.timezone) } : null;
}

function asCatalogRef(value: unknown): TransferRecordCatalogRef | null {
  const object = asObject(value);
  const id = asText(object?.id);
  return id ? { id, code: asText(object?.code), name: asText(object?.name) } : null;
}

function asDocRef(value: unknown): TransferRecordDocRef | null {
  const object = asObject(value);
  const id = asText(object?.id);
  return id ? { id, docNo: asText(object?.doc_no), status: String(object?.status ?? '').trim().toLowerCase() } : null;
}

const LINE_KINDS: readonly TransferLineKind[] = ['item', 'product', 'asset', 'vehicle', 'external', 'accessory'];
const SERIAL_STATUSES: readonly TransferSerialStatus[] = ['assigned', 'picked', 'shipped', 'in_transit', 'received', 'kept', 'cancelled'];

function parseLineKind(value: unknown): TransferLineKind {
  const text = String(value ?? '').trim().toLowerCase();
  return (LINE_KINDS as readonly string[]).includes(text) ? text as TransferLineKind : 'item';
}

function parseSerialStatus(value: unknown): TransferSerialStatus {
  const text = String(value ?? '').trim().toLowerCase();
  return (SERIAL_STATUSES as readonly string[]).includes(text) ? text as TransferSerialStatus : 'assigned';
}

function parseHeader(value: unknown): TransferRecordHeader | null {
  const object = asObject(value);
  const id = asText(object?.id);
  if (!object || !id) return null;
  const returnLegObject = asObject(object.return_leg);
  const returnLegRef = asDocRef(returnLegObject);
  const requestObject = asObject(object.request);
  return {
    id,
    docNo: asText(object.doc_no),
    status: String(object.status ?? '').trim().toLowerCase(),
    source: object.source === 'r2' ? 'r2' : 'portal',
    description: asText(object.description),
    scope: object.scope === 'permanent' ? 'permanent' : 'temporary',
    fromSite: asSite(object.from_site),
    toSite: asSite(object.to_site),
    needByAt: asText(object.need_by_at),
    expectedShipAt: asText(object.expected_ship_at),
    expectedReceiveAt: asText(object.expected_receive_at),
    expectedReturnAt: asText(object.expected_return_at),
    actualShipAt: asText(object.actual_ship_at),
    actualReceiveAt: asText(object.actual_receive_at),
    confirmedAt: asText(object.confirmed_at),
    createdAt: asText(object.created_at),
    createdBy: asRef(object.created_by),
    logisticsMode: asRef(object.logistics_mode),
    trackingNo: asText(object.tracking_no),
    lastR2ImportAt: asText(object.last_r2_import_at),
    daysLate: asNullableNumber(object.days_late),
    parent: asDocRef(object.parent),
    returnLeg: returnLegRef
      ? {
        ...returnLegRef,
        expectedShipAt: asText(returnLegObject?.expected_ship_at),
        lineCount: asNumber(returnLegObject?.line_count),
      }
      : null,
    request: requestObject
      ? {
        broadcastId: asText(requestObject.broadcast_id),
        itemName: asText(requestObject.item_name),
        qty: asNullableNumber(requestObject.qty),
        requestedBySite: asRef(requestObject.requested_by_site),
        requestedAt: asText(requestObject.requested_at),
        acceptedBySite: asRef(requestObject.accepted_by_site),
        acceptedAt: asText(requestObject.accepted_at),
        status: String(requestObject.status ?? '').trim().toLowerCase(),
      }
      : null,
    chatConversationId: asText(object.chat_conversation_id),
    chatMessageCount: asNullableNumber(object.chat_message_count),
  };
}

function parseTotals(value: unknown): TransferRecordTotals {
  const object = asObject(value) ?? {};
  return {
    lineCount: asNumber(object.line_count),
    units: asNumber(object.units),
    picked: asNumber(object.picked),
    shipped: asNumber(object.shipped),
    received: asNumber(object.received),
    outstanding: asNumber(object.outstanding),
    serializedAssets: asNumber(object.serialized_assets),
    shipmentCount: asNumber(object.shipment_count),
  };
}

function parseSerial(value: unknown): TransferRecordSerial | null {
  const object = asObject(value);
  const assetId = asText(object?.asset_id);
  const id = asText(object?.id) ?? assetId;
  if (!object || !id) return null;
  const code = asText(object.code) ?? assetId?.slice(0, 8) ?? 'Unit';
  const codes = asArray(object.codes).map(asText).filter((value): value is string => value != null);
  return {
    id,
    assetId,
    code,
    codes: codes.includes(code) ? codes : [code, ...codes],
    status: parseSerialStatus(object.status),
  };
}

function parseLine(value: unknown): TransferRecordLine | null {
  const object = asObject(value);
  const id = asText(object?.id);
  if (!object || !id) return null;
  const sourceEventObject = asObject(object.source_event);
  const sourceEventId = asText(sourceEventObject?.id);
  return {
    id,
    kind: parseLineKind(object.kind),
    item: asCatalogRef(object.item),
    product: asCatalogRef(object.product),
    vehicle: asRef(object.vehicle),
    description: asText(object.description),
    category: asText(object.category),
    tracking: object.tracking === 'serial' ? 'serial' : 'quantity',
    qty: asNumber(object.qty),
    pickedQty: asNumber(object.picked_qty),
    shippedQty: asNumber(object.shipped_qty),
    receivedQty: asNumber(object.received_qty),
    shortQty: asNumber(object.short_qty),
    status: String(object.status ?? '').trim().toLowerCase() || 'pending',
    serials: asArray(object.serials)
      .map(parseSerial)
      .filter((serial): serial is TransferRecordSerial => serial != null),
    sourceEvent: sourceEventId ? { id: sourceEventId, code: asText(sourceEventObject?.code) } : null,
    notes: asText(object.notes),
  };
}

function parseWaypoint(value: unknown): TransferRecordWaypoint | null {
  const object = asObject(value);
  const id = asText(object?.id);
  if (!object || !id) return null;
  return {
    id,
    seq: asNumber(object.seq),
    location: asRef(object.location),
    address: asText(object.address),
    kind: asText(object.kind),
    status: String(object.status ?? '').trim().toLowerCase() || 'pending',
    arriveAt: asText(object.arrive_at),
    departAt: asText(object.depart_at),
    notes: asText(object.notes),
  };
}

function parseShipment(value: unknown): TransferRecordShipment | null {
  const object = asObject(value);
  const id = asText(object?.id);
  if (!object || !id) return null;
  return {
    id,
    carrier: asText(object.carrier),
    mode: asText(object.mode),
    trackingNo: asText(object.tracking_no),
    vehicle: asRef(object.vehicle),
    driverName: asText(object.driver_name),
    shippedAt: asText(object.shipped_at),
    deliveredAt: asText(object.delivered_at),
    notes: asText(object.notes),
  };
}

function parseEvent(value: unknown): TransferRecordEvent | null {
  const object = asObject(value);
  const id = asText(object?.id);
  if (!object || !id) return null;
  return {
    id,
    code: asText(object.code),
    name: asText(object.name),
    startDate: asText(object.start_date),
    endDate: asText(object.end_date),
    site: asRef(object.site),
    status: asText(object.status),
    unitsOnTransfer: asNullableNumber(object.units_on_transfer),
    // A reader without these flags lists linked events only.
    linked: object.linked !== false,
    onLines: object.on_lines === true,
  };
}

function parseTimelineEntry(value: unknown): TransferTimelineEntry | null {
  const object = asObject(value);
  const id = asText(object?.id);
  const at = asText(object?.at);
  if (!object || !id || !at) return null;
  return {
    id,
    at,
    kind: asText(object.kind) ?? 'activity',
    summary: asText(object.summary) ?? '',
    actor: asRef(object.actor),
    details: asObject(object.details),
  };
}

function parseActions(value: unknown): TransferRecordActions {
  const object = asObject(value) ?? {};
  const actions = {} as TransferRecordActions;
  for (const name of TRANSFER_ACTION_NAMES) {
    const entry = asObject(object[name]);
    actions[name] = { allowed: entry?.allowed === true, reason: asText(entry?.reason) };
  }
  return actions;
}

/** Reads `get_transfer_record`'s jsonb; anything missing or malformed falls back to empty values. */
export function parseTransferRecord(data: unknown): TransferRecord | null {
  const object = asObject(data);
  const transfer = parseHeader(object?.transfer);
  if (!object || !transfer) return null;
  const waypoints = asArray(object.waypoints)
    .map(parseWaypoint)
    .filter((waypoint): waypoint is TransferRecordWaypoint => waypoint != null)
    .sort((left, right) => left.seq - right.seq);
  const timeline = asArray(object.timeline)
    .map(parseTimelineEntry)
    .filter((entry): entry is TransferTimelineEntry => entry != null)
    .sort((left, right) => Date.parse(right.at) - Date.parse(left.at));
  return {
    transfer,
    totals: parseTotals(object.totals),
    lines: asArray(object.lines).map(parseLine).filter((line): line is TransferRecordLine => line != null),
    waypoints,
    shipments: asArray(object.shipments).map(parseShipment).filter((shipment): shipment is TransferRecordShipment => shipment != null),
    events: asArray(object.events).map(parseEvent).filter((event): event is TransferRecordEvent => event != null),
    timeline,
    actions: parseActions(object.actions),
  };
}

export const transferRecordQueryKeys = {
  record: (transferId: string) => ['warehouse', 'transfer-record', transferId] as const,
  vehicles: (locationId: string) => ['warehouse', 'transfer-record', 'fleet-vehicles', locationId] as const,
};

type PostgrestLikeError = { message?: unknown; hint?: unknown; code?: unknown; details?: unknown };

function errorField(error: unknown, key: keyof PostgrestLikeError): string | null {
  if (!error || typeof error !== 'object') return null;
  const value = (error as PostgrestLikeError)[key];
  return typeof value === 'string' && value.trim() ? value.trim() : null;
}

/** A Warehouse refusal key ("transfer_ship_requires_full_pick"), carried in the error's hint. */
const REFUSAL_KEY = /^(?:app_)?(?:transfer|warehouse)_[a-z0-9_]+$/;

/**
 * A refused or failed transfer action. `message` is safe to show: the server's own sentence for a
 * Warehouse refusal, otherwise the app's readable wording. `key` is the machine key (from the hint).
 * `answered`: the server answered (a refusal or an error inside the call, so the call rolled back and
 * changed nothing). False when no answer came back (connection lost, timeout, a gateway error): the
 * call may or may not have applied, so only a retry with the same payload and request key is safe.
 */
export class TransferActionError extends Error {
  readonly key: string | null;
  readonly sqlstate: string | null;
  readonly detail: string | null;
  readonly answered: boolean;

  constructor(
    message: string,
    options: { key?: string | null; sqlstate?: string | null; detail?: string | null; answered?: boolean } = {},
  ) {
    super(message);
    this.name = 'TransferActionError';
    this.key = options.key ?? null;
    this.sqlstate = options.sqlstate ?? null;
    this.detail = options.detail ?? null;
    this.answered = options.answered ?? Boolean(this.sqlstate);
  }
}

export function toTransferActionError(
  error: unknown,
  fallback: string,
  source: 'query' | 'mutation' = 'mutation',
): TransferActionError {
  if (error instanceof TransferActionError) return error;
  const hint = errorField(error, 'hint');
  const message = errorField(error, 'message');
  const code = errorField(error, 'code');
  const key = hint && REFUSAL_KEY.test(hint) ? hint : null;
  const readable = key && message && !REFUSAL_KEY.test(message)
    ? message
    : readableErrorMessage(error, fallback, source);
  return new TransferActionError(readable, {
    key,
    sqlstate: code,
    detail: errorField(error, 'details'),
    // PostgREST answers with a code (SQLSTATE or PGRST...); a lost connection, a timeout or a gateway
    // page has none. A request still running under the same key is no answer either.
    answered: Boolean(code) && hint !== 'warehouse_request_in_progress',
  });
}

/** True when the action got no answer, so it may have applied (retry it unchanged, same key). */
export function transferActionUnanswered(error: unknown): boolean {
  return error instanceof TransferActionError && !error.answered;
}

/** The message to show for any failure of a transfer action. */
export function transferActionErrorMessage(error: unknown, fallback: string): string {
  return toTransferActionError(error, fallback).message;
}

/** A new request key; send the same one again only when retrying the same action. */
export function newTransferRequestKey(): string {
  const cryptoApi = typeof globalThis !== 'undefined' ? globalThis.crypto : undefined;
  if (cryptoApi && typeof cryptoApi.randomUUID === 'function') return cryptoApi.randomUUID();
  const bytes = new Uint8Array(16);
  if (cryptoApi && typeof cryptoApi.getRandomValues === 'function') {
    cryptoApi.getRandomValues(bytes);
  } else {
    for (let index = 0; index < bytes.length; index += 1) bytes[index] = Math.floor(Math.random() * 256);
  }
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

/** Loads the record; null when the transfer does not exist (or the viewer cannot see it). */
export async function fetchTransferRecord(transferId: string): Promise<TransferRecord | null> {
  const { data, error } = await getSupabaseClient().rpc('get_transfer_record', { p_transfer_id: transferId });
  if (error) {
    if (errorField(error, 'hint') === 'transfer_not_found' || errorField(error, 'code') === 'P0002') return null;
    // A refusal ("This transfer is not at one of your sites.") keeps the server's words.
    throw toTransferActionError(error, 'The transfer could not be loaded. Try again.', 'query');
  }
  return parseTransferRecord(data);
}

/** What every action returns: `{ok, id, status, replayed, …}`; the rest stays in `raw`. */
export type TransferActionResult = {
  ok: boolean;
  id: string | null;
  status: string | null;
  replayed: boolean;
  raw: Json;
};

function parseActionResult(data: unknown): TransferActionResult {
  const raw = asObject(data) ?? {};
  return {
    ok: raw.ok !== false,
    id: asText(raw.id) ?? asText(raw.transfer_id),
    status: asText(raw.status),
    replayed: raw.replayed === true,
    raw,
  };
}

async function callTransferAction(name: string, args: Json, fallback: string): Promise<TransferActionResult> {
  const { data, error } = await getSupabaseClient().rpc(name, args);
  if (error) throw toTransferActionError(error, fallback);
  return parseActionResult(data);
}

export type TransferDraftLineAdd = {
  item_id?: string;
  product_id?: string;
  vehicle_id?: string;
  external_name?: string;
  /** External lines only: the code printed on the item, when there is one. */
  external_code?: string | null;
  qty: number;
  asset_ids?: string[];
  /** External lines only: an accessory rather than a not-in-catalog item. */
  accessory?: boolean;
  notes?: string | null;
};

export type TransferDraftPatch = {
  need_by_at?: string | null;
  expected_ship_at?: string | null;
  expected_receive_at?: string | null;
  expected_return_at?: string | null;
  description?: string | null;
  scope?: TransferScope;
  lines?: {
    add?: TransferDraftLineAdd[];
    update?: Array<{ line_id: string; qty?: number; notes?: string | null }>;
    remove?: string[];
  };
};

export function updateTransferDraft(transferId: string, requestKey: string, patch: TransferDraftPatch) {
  return callTransferAction('transfer_update_draft', {
    p_transfer_id: transferId,
    p_request_key: requestKey,
    p_patch: patch,
  }, 'The transfer was not changed.');
}

export function confirmTransfer(transferId: string, requestKey: string) {
  return callTransferAction('transfer_confirm', {
    p_transfer_id: transferId,
    p_request_key: requestKey,
  }, 'The transfer was not confirmed.');
}

/** `{line_id, qty}` sets a quantity line's picked units (absolute); `{asset_id}` or `{code}` picks one unit. */
export type TransferPickOp =
  | { line_id: string; qty: number }
  | { asset_id: string; line_id?: string }
  | { code: string; line_id?: string }
  | { remove_asset_id: string };

export type TransferPickOpResult = {
  opIndex: number;
  ok: boolean;
  message: string | null;
  key: string | null;
  lineId: string | null;
};

function parsePickResults(raw: Json): TransferPickOpResult[] | null {
  if (!Array.isArray(raw.results)) return null;
  return raw.results
    .map((value, index) => {
      const object = asObject(value);
      if (!object) return null;
      const opIndex = asNullableNumber(object.op_index ?? object.op);
      return {
        opIndex: opIndex == null ? index : opIndex,
        ok: object.ok !== false,
        message: asText(object.message),
        key: asText(object.code),
        lineId: asText(object.line_id),
      };
    })
    .filter((result): result is TransferPickOpResult => result != null);
}

/** Picks; each operation applies on its own. `results` is null when the server did not report them. */
export async function pickTransfer(
  transferId: string,
  requestKey: string,
  ops: TransferPickOp[],
): Promise<TransferActionResult & { results: TransferPickOpResult[] | null }> {
  const result = await callTransferAction('transfer_pick', {
    p_transfer_id: transferId,
    p_request_key: requestKey,
    p_ops: ops,
  }, 'Nothing was picked.');
  return { ...result, results: parsePickResults(result.raw) };
}

export type TransferShipmentInput = {
  carrier?: string | null;
  mode?: string | null;
  tracking_no?: string | null;
  vehicle_id?: string | null;
  driver_name?: string | null;
  shipped_at?: string | null;
  delivered_at?: string | null;
  notes?: string | null;
};

export function shipTransfer(
  transferId: string,
  requestKey: string,
  options: { shipShort: boolean; shipment: TransferShipmentInput | null },
) {
  return callTransferAction('transfer_ship', {
    p_transfer_id: transferId,
    p_request_key: requestKey,
    p_ship_short: options.shipShort,
    p_shipment: options.shipment,
  }, 'The transfer was not shipped.');
}

/** One short line from a `transfer_ship_requires_full_pick` refusal (its detail). */
export type TransferShortLine = { lineId: string; orderedQty: number; pickedQty: number; shortQty: number };

export function parseShortLines(detail: string | null): TransferShortLine[] {
  if (!detail) return [];
  try {
    return asArray(JSON.parse(detail))
      .map((value) => {
        const object = asObject(value);
        const lineId = asText(object?.line_id);
        if (!object || !lineId) return null;
        return {
          lineId,
          orderedQty: asNumber(object.ordered_qty),
          pickedQty: asNumber(object.picked_qty),
          shortQty: asNumber(object.short_qty),
        };
      })
      .filter((line): line is TransferShortLine => line != null);
  } catch {
    return [];
  }
}

export type TransferReceiveLine = { line_id: string; qty: number } | { line_id: string; asset_ids: string[] };

export function receiveTransfer(transferId: string, requestKey: string, lines: TransferReceiveLine[], note: string | null) {
  return callTransferAction('transfer_receive', {
    p_transfer_id: transferId,
    p_request_key: requestKey,
    p_lines: lines,
    p_note: note,
  }, 'Nothing was received.');
}

export function cancelTransfer(transferId: string, requestKey: string, reason: string | null) {
  return callTransferAction('transfer_cancel', {
    p_transfer_id: transferId,
    p_request_key: requestKey,
    p_reason: reason,
  }, 'The transfer was not cancelled.');
}

export async function createTransferReturnLeg(transferId: string, requestKey: string, shipAt: string | null) {
  const result = await callTransferAction('transfer_create_return_leg', {
    p_transfer_id: transferId,
    p_request_key: requestKey,
    p_ship_at: shipAt,
  }, 'The return leg was not created.');
  return { ...result, returnLeg: asDocRef(result.raw.return_leg) };
}

/** Kinds the server accepts for a reported problem. */
export type TransferProblemKind = 'damaged' | 'missing' | 'wrong_item' | 'extra_item' | 'late' | 'other';

export function reportTransferProblem(
  transferId: string,
  requestKey: string,
  problem: { kind: TransferProblemKind; note: string; lineId?: string | null; assetId?: string | null },
) {
  return callTransferAction('transfer_report_problem', {
    p_transfer_id: transferId,
    p_request_key: requestKey,
    p_kind: problem.kind,
    p_note: problem.note,
    p_line_id: problem.lineId ?? null,
    p_asset_id: problem.assetId ?? null,
  }, 'The problem was not noted.');
}

export function saveTransferShipment(
  transferId: string,
  requestKey: string,
  shipment: TransferShipmentInput & { id?: string | null; delete?: boolean },
) {
  return callTransferAction('transfer_save_shipment', {
    p_transfer_id: transferId,
    p_request_key: requestKey,
    p_shipment: shipment,
  }, 'The shipment was not saved.');
}

export type TransferStopKind = 'pickup' | 'dropoff' | 'stop' | 'storage' | 'cross_dock';

export type TransferStopInput = {
  id?: string | null;
  location_id?: string | null;
  address?: string | null;
  kind?: TransferStopKind | null;
  arrive_at?: string | null;
  depart_at?: string | null;
  notes?: string | null;
  delete?: boolean;
};

export function saveTransferStop(transferId: string, requestKey: string, stop: TransferStopInput) {
  return callTransferAction('transfer_save_stop', {
    p_transfer_id: transferId,
    p_request_key: requestKey,
    p_stop: stop,
  }, 'The stop was not saved.');
}

export function setTransferEventLink(transferId: string, requestKey: string, eventId: string, linked: boolean) {
  return callTransferAction('transfer_set_event_link', {
    p_transfer_id: transferId,
    p_request_key: requestKey,
    p_event_id: eventId,
    p_linked: linked,
  }, linked ? 'The event was not linked.' : 'The event was not unlinked.');
}

export type TransferStopProgress = 'arrived' | 'available' | 'picked' | 'shipped';

/**
 * Moves a stop along (arrived → available → picked → left) through `transfer_save_stop`, with the
 * same site and R2 rules, request key and History event as any stop change. The server stamps the
 * arrival or departure time only when the step has none yet, so the arrival survives later steps;
 * "available" moves the transfer's shipped lines in transit and is refused on R2 transfers.
 */
export function setTransferStopProgress(transferId: string, requestKey: string, stopId: string, status: TransferStopProgress) {
  return callTransferAction('transfer_save_stop', {
    p_transfer_id: transferId,
    p_request_key: requestKey,
    p_stop: { id: stopId, status },
  }, 'The stop was not updated.');
}

export type TransferFleetVehicle = { id: string; name: string; type: string | null; plate: string | null };

/** Active fleet vehicles at a site, for vehicle lines and shipments. */
export async function fetchTransferFleetVehicles(locationId: string): Promise<TransferFleetVehicle[]> {
  const { data, error } = await getSupabaseClient()
    .from('location_fleet_vehicles')
    .select('vehicle_id,name,vehicle_type,license_plate')
    .eq('current_location_id', locationId)
    .eq('is_active', true)
    .order('name', { ascending: true })
    .limit(200);
  if (error) throw error;
  return asArray(data)
    .map((row) => {
      const object = asObject(row);
      const id = asText(object?.vehicle_id);
      if (!object || !id) return null;
      return {
        id,
        name: asText(object.name) ?? 'Vehicle',
        type: asText(object.vehicle_type),
        plate: asText(object.license_plate),
      };
    })
    .filter((vehicle): vehicle is TransferFleetVehicle => vehicle != null);
}
